import { useEffect, useState } from 'react';
import { absenceApi } from '../../../api/absence.js';

/**
 * Accrual policy admin — list/create policies (method monthly|annual|
 * per_hours_worked|tenure_tiered, rate, max balance, carryover cap) and a
 * "Run accruals" button that posts the current period's accrual ledger rows.
 */
const METHODS = ['monthly', 'annual', 'per_hours_worked', 'tenure_tiered'];

export default function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  function refresh() {
    setLoading(true);
    Promise.all([absenceApi.listPolicies(), absenceApi.listPlans({ active: true })])
      .then(([po, pl]) => {
        setPolicies(po?.items || []);
        setPlans(pl?.items || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load policies'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function createPolicy(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await absenceApi.createPolicy({
        absencePlanId: fd.get('absencePlanId'),
        method: fd.get('method'),
        rate: fd.get('rate') ? Number(fd.get('rate')) : 0,
        maxBalance: fd.get('maxBalance') ? Number(fd.get('maxBalance')) : null,
        carryoverMax: fd.get('carryoverMax') ? Number(fd.get('carryoverMax')) : null,
      });
      e.target.reset();
      setShowCreate(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create policy');
    }
  }

  async function removePolicy(id) {
    try {
      await absenceApi.deletePolicy(id);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete policy');
    }
  }

  async function runAccruals() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await absenceApi.runAccruals({});
      setNotice(
        `Accruals run for ${res.period}: ${res.accrued} accrued, ${res.skipped} skipped` +
          (res.forfeited ? `, ${res.forfeited} forfeited (${res.forfeitedAmount})` : '')
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to run accruals');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="absence-policies-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Accrual Policies</h1>
          <p className="muted">{policies.length} policies</p>
        </div>
        <div>
          <button className="btn" data-testid="absence-accruals-run" onClick={runAccruals} disabled={busy}>Run accruals</button>
          <button className="btn primary" data-testid="absence-policy-create" onClick={() => setShowCreate((s) => !s)}>
            {showCreate ? 'Close' : '+ Policy'}
          </button>
        </div>
      </header>

      {error && <div className="alert error" data-testid="absence-policies-error">{error}</div>}
      {notice && <div className="alert info" data-testid="absence-policies-notice">{notice}</div>}

      {showCreate && (
        <form onSubmit={createPolicy} className="card form-card grid-form">
          <label>Plan
            <select name="absencePlanId" data-testid="absence-policy-plan-input" required defaultValue="">
              <option value="" disabled>— select plan —</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label>Method
            <select name="method" defaultValue="monthly" data-testid="absence-policy-method-input">
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Rate<input name="rate" type="number" min="0" step="0.01" data-testid="absence-policy-rate-input" /></label>
          <label>Max balance<input name="maxBalance" type="number" min="0" step="0.01" data-testid="absence-policy-maxbalance-input" /></label>
          <label>Carryover max<input name="carryoverMax" type="number" min="0" step="0.01" data-testid="absence-policy-carryover-input" /></label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="absence-policy-submit">Create policy</button></div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="absence-policy-list">
          <thead><tr><th>Plan</th><th>Method</th><th>Rate</th><th>Max</th><th>Carryover</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} data-testid={`absence-policy-row-${p.id}`}>
                <td>{p.plan_name}</td>
                <td>{p.method}</td>
                <td>{Number(p.rate)}</td>
                <td>{p.max_balance == null ? '—' : Number(p.max_balance)}</td>
                <td>{p.carryover_max == null ? '—' : Number(p.carryover_max)}</td>
                <td>{p.active ? 'yes' : 'no'}</td>
                <td><button className="btn ghost" data-testid={`absence-policy-delete-${p.id}`} onClick={() => removePolicy(p.id)}>Delete</button></td>
              </tr>
            ))}
            {!loading && policies.length === 0 && <tr><td colSpan={7} className="muted">No policies yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
